import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { RelatedReading } from "@/components/RelatedReading";
import { PageToolbar, UnsplashCredit, usePageTranslation } from "@/components/PageToolbar";

const topicNav = [
  { slug: "studios-production", label: "Studios & Production" },
  { slug: "theaters-where-to-watch", label: "Theaters & Where to Watch" },
  { slug: "film-schools-education", label: "Film Schools & Education" },
  { slug: "halls-of-fame-history", label: "Halls of Fame & History" },
  { slug: "business-box-office-technology", label: "Business & Technology" },
  { slug: "genres-languages", label: "Genres & Languages" },
  { slug: "documentary-cinema", label: "Documentary Cinema" },
  { slug: "animation-worldwide", label: "Animation Worldwide" },
  { slug: "women-in-film", label: "Women in Film" },
  { slug: "restoration-preservation", label: "Restoration & Preservation" },
  { slug: "short-films-student-cinema", label: "Shorts & Student Cinema" },
  { slug: "sound-music-in-cinema", label: "Sound & Music in Cinema" },
];

export default function TopicPage() {
  const { slug } = useParams();
  const [topic, setTopic] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    setTopic(null);
    setLoading(true);
    api
      .get(`/topics/${slug}`, { timeout: 90000 })
      .then((r) => setTopic(r.data))
      .finally(() => setLoading(false));
  }, [slug]);

  const blocks = useMemo(() => {
    if (!topic?.content) return null;
    const b = { tagline: topic.tagline };
    topic.content.split("\n").filter((l) => l.trim()).forEach((line, i) => (b[`l${i}`] = line));
    return b;
  }, [topic]);
  const { lang, tr, busy } = usePageTranslation(blocks, `topic-${slug}`);

  if (loading)
    return (
      <main className="max-w-3xl mx-auto px-6 py-24 text-center">
        <Sparkles className="w-8 h-8 text-[#b06810] mx-auto animate-pulse" />
        <p className="font-display text-2xl text-[#6b5d51] mt-4">Our editorial AI is preparing this guide…</p>
        <p className="text-sm text-[#8a7c6d] mt-2">First visits take a few seconds while the content is written.</p>
      </main>
    );

  if (!topic) return <main className="max-w-3xl mx-auto px-6 py-24 text-center font-display text-2xl text-[#6b5d51]">Topic not found.</main>;

  const lines = topic.content.split("\n").filter((l) => l.trim());
  const speakText = [topic.title, ...lines.map((l, i) => tr(`l${i}`, l).replace(/^##\s*/, ""))].join(". ");

  return (
    <main data-testid="topic-page" className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
      <p className="eyebrow">Topic Guide</p>
      <h1 data-testid="topic-title" className="font-display font-bold text-4xl sm:text-5xl mt-2 text-[#1c1512]">{topic.title}</h1>
      <p className="font-script text-2xl text-[#b06810] mt-2">{tr("tagline", topic.tagline)}</p>

      <div className="mt-6 flex flex-wrap items-center gap-4">
        <PageToolbar speakText={speakText} busy={busy} lang={lang} />
        {topic.ai_generated && (
          <span className="flex items-center gap-1.5 font-mono-label text-[10px] tracking-[0.18em] uppercase text-[#8a7c6d]">
            <Sparkles className="w-3.5 h-3.5 text-[#b06810]" /> AI-assisted editorial · reviewed format
          </span>
        )}
      </div>

      {topic.photo && (
        <figure className="mt-8">
          <div className="aspect-[16/8] overflow-hidden bg-[#171210]">
            <img src={topic.photo.url} alt={topic.photo.alt} className="w-full h-full object-cover" />
          </div>
          <UnsplashCredit photo={topic.photo} className="mt-2" />
        </figure>
      )}

      <article data-testid="topic-content" className="mt-8">
        {lines.map((line, i) => {
          const text = tr(`l${i}`, line);
          if (text.startsWith("## "))
            return (
              <h2 key={i} className="font-display font-bold text-2xl mt-10 mb-3 rule-thick pt-4 text-[#1c1512]">
                {text.replace(/^##\s*/, "")}
              </h2>
            );
          return (
            <p key={i} className="text-lg leading-relaxed text-[#2b241d] mb-5">
              {text}
            </p>
          );
        })}
      </article>

      <div className="mt-12">
        <RelatedReading articles={topic.related_articles} />
      </div>

      <div className="mt-14 rule-thick pt-6">
        <p className="eyebrow mb-4">More Topic Guides</p>
        <div className="flex flex-wrap gap-2">
          {topicNav.filter((t) => t.slug !== slug).map((t) => (
            <Link key={t.slug} to={`/topics/${t.slug}`} data-testid={`topic-nav-${t.slug}`} className="bg-[#fbf8f1] border border-[#d8ccb8] rounded-full px-4 py-2 text-sm hover:border-[#7d1a1a] hover:text-[#7d1a1a] transition-colors">
              {t.label}
            </Link>
          ))}
        </div>
      </div>
    </main>
  );
}
